import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../../../axios";

import {
  FiMenu,
  FiChevronRight,
  FiShield,
  FiBell,
  FiCreditCard,
  FiUser,
  FiLock,
  FiHome,
} from "react-icons/fi";

import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { HiOutlineBuildingLibrary } from "react-icons/hi2";
import { MdOutlineEdit } from "react-icons/md";
import { UserCircle, WalletIcon, GraduationCap } from "lucide-react";
import { FaAngleLeft } from "react-icons/fa";
import { getInstituteName, useInstitute } from "../../../hooks/use-institute";

const ProfileNew = () => {
  const [user, setUser] = useState(null);
  const [selectedExamName, setSelectedExamName] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const handlePrevPage = () => {
  navigate(-1); // goes back to previous page
};

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("me");
        const userData = data?.result || null;
        setUser(userData);

        const selectedExamId =
          userData?.selectedExam?._id ||
          userData?.selectedExam ||
          localStorage.getItem("examId");

        if (selectedExamId) {
          try {
            const selectedState =
              userData?.selectedState?._id ||
              userData?.selectedState ||
              localStorage.getItem("stateId");

            const examRes = await axios.get(
              selectedState ? `/exams/all?stateId=${selectedState}` : "/exams/all"
            );

            const examList =
              examRes?.data?.result ||
              examRes?.data?.data ||
              examRes?.data ||
              [];

            const matchedExam = Array.isArray(examList)
              ? examList.find(
                  (exam) =>
                    String(exam?._id || exam?.id || exam?.examId) ===
                    String(selectedExamId)
                )
              : null;

            const examName =
              matchedExam?.examName ||
              matchedExam?.name ||
              matchedExam?.title ||
              localStorage.getItem("examName") ||
              "Selected Exam";

            setSelectedExamName(examName);
          } catch (examError) {
            setSelectedExamName(
              localStorage.getItem("examName") || "Selected Exam"
            );
          }
        } else {
          setSelectedExamName(localStorage.getItem("examName") || "");
        }
      } catch (error) {
        console.error("Failed to load profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const questionSolved = (user?.quizStats?.totalRight || 0) + (user?.quizStats?.totalWrong || 0);
  const joinedContests = user?.joinedContests?.length || 0;
  const userInstituteId =
    (typeof user?.instituteId === "string" && user.instituteId.trim()) ||
    (typeof user?.libraryId === "string" && user.libraryId.trim()) ||
    "";
  const { data: institute } = useInstitute(userInstituteId);
  const instituteName =
    getInstituteName(institute) || user?.instituteName || "Organisation";
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "US";

  const kycStatus = user?.kyc?.status ;
  const isKycVerified =
    user?.kyc?.status ||
    ["verified", "approved", "completed"].includes(String(kycStatus).toLowerCase());
      
  const profileItems = [
    {
      title: "Personal Information",
      subtitle: `${user?.email || "user@example.com"} • ${
        user?.mobile || "0123456789"
      }`,
      icon: <UserCircle className="h-5 w-7 text-[#5B2A6D]" />,
      path:"/UserDetailPage",
    },
     {
      title: "Select Exam",
      subtitle: selectedExamName || "Choose your exam preparation track",
      icon: <GraduationCap className="h-5 w-7 text-[#5B2A6D]" />,
      path: "/dash/select-exam",
    },
    {
      title: "Organisation",
      subtitle: userInstituteId
        ? `${instituteName} | ${userInstituteId}`
        : "No organisation linked",
      icon: <HiOutlineBuildingLibrary className="h-5 w-7 text-[#5B2A6D]" />,
      path: "/dash/organisation",
    },
    {
      title: "KYC Status",
      subtitle: kycStatus,
      isVerified: isKycVerified,
      icon: <FiShield className="h-5 w-7 text-[#5B2A6D]" />,
      path: "/dash/kyc-status",
    },
    {
      title: "Bank Setting",
      subtitle: user?.bank?.bankName
        ? `${user.bank.bankName} • ${user.bank.upi || "UPI"}`
        : "Add bank and UPI details",
      icon: <HiOutlineBuildingLibrary className="h-5 w-7 text-[#5B2A6D]" />,
      path: "/dash/bank-details",
    },
   
    {
      title: "Security",
      subtitle: user?.mobile
        ? `${user.mobile} verified`
        : "Phone not verified",
      icon: <FiLock className="h-5 w-7 text-[#5B2A6D]" />,
      path: "/dash/security",
    },
  ];

  return (
    <div className=" bg-[#F7F4FB] min-h-screen pb-24 mt-0">
      {/* HEADER */}
      <div className="relative rounded-b-[50px] bg-[#4B1D67] px-5 pb-16 lg:px-10 lg:pb-20">
        
        

        {/* PROFILE */}
        <div className="lg:mt-10 pt-8 flex flex-col items-center justify-center text-center text-white">
          
          {/* AVATAR */}
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-[#E8DCC8] bg-[#F2A81D] text-3xl font-bold text-white shadow-lg overflow-hidden">
              {loading ? (
                <AiOutlineLoading3Quarters className="animate-spin" />
              ) : (
                initials
              )}
            </div>

            {/* EDIT BUTTON */}
            <Link to="/UserDetailPage">
              <button className="absolute bottom-0.5 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-gray-500 text-white shadow-md">
                <MdOutlineEdit className="h-4 w-4" />
              </button>
            </Link>
          </div>

          {/* NAME */}
          <h1 className="mt-2 text-xl font-bold">
            {user?.name || "User Name"}
          </h1>

          <p className="text-lg text-white/80">
            master •{user?.userState || "Madhya Pradesh"}
          </p>

          {/* PLAN */}
          <div className="mt-2 rounded-full bg-[#ECE6EF] px-8 py-1 text-lg font-semibold text-black shadow-md">
            {user?.planName || "Government Exam Combo Plan"}
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="relative z-50 -mt-14 grid grid-cols-2 gap-5 px-6 z-10 lg:max-w-3xl lg:mx-auto">
        
        <div className="rounded-[28px] bg-[#F3E8D1] px-4 py-2 text-center shadow-md lg:px-5 lg:py-4">
          <p className="lg:text-xl sm:text-sm text-black">Total Questions Solved</p>

          <h2 className="mt-1 text-2xl font-bold text-black">
            {questionSolved}
          </h2>
        </div>

        <div className="rounded-[28px] bg-[#F3E8D1] px-4 py-2 text-center shadow-md lg:px-5 lg:py-4">
          <p className="lg:text-xl sm:text-sm text-black">Contests Joined</p>

          <h2 className="mt-1 text-2xl font-bold text-black">
            {joinedContests}
          </h2>
        </div>
      </div>

      {/* PROFILE OPTIONS */}
<div className="mt-4 grid gap-3 px-3 sm:px-4  md:grid-cols-2 lg:px-10 max-w-[420px] mx-auto md:max-w-full">      
    {profileItems.map((item) => (
          <button
            key={item.title}
            type="button"
            onClick={() => {
              if (item.path) {
                navigate(item.path);
              }
            }}
            className="w-full rounded-[26px] border border-[#B6A9C3] bg-[#E9E1EE] px-2 py-2 sm:px-4 sm:py-3 shadow-sm text-left"
          >
            <div className="flex items-center justify-between">
              
              {/* LEFT */}
              <div className="flex items-center gap-3">
                
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#D2C5DB]">
                  {item.icon}
                </div>

                <div className="text-left">
                  <h3 className="lg:text-xl sm:text-sm font-bold text-black leading-tight">
                    {item.title}
                  </h3>

                  <p
                    className={`lg:text-md sm:text-sm leading-tight ${
                      item.title === "KYC Status"
                        ? item.isVerified === "verified"
                          ? "font-semibold text-green-600"
                          : "text-[#B36B2E]"
                        : "text-[#2E2437]"
                    }`}
                  >
                    {item.subtitle}
                  </p>
                </div>
              </div>

              {/* RIGHT ICON */}
              <FiChevronRight className="h-6 w-6 text-gray-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProfileNew;
